import { lang, isSystem } from '@umu/ukit-lang';
/**
 * @description userMenu
 * @author duanjun
 */

import React, { useState, useMemo, useCallback } from 'react';
import { getData, isInUse, USEQuery, setIsInUse } from '@umu/umu-util';
import { getMHost, isCanShowFontSizeSwitch, isFromMideaMiniApp } from '../../@utils/umuUtil';
import { If, Portal } from 'common/src/components';

import IUserMenuController from './userMenuController';
import UserAvatar from '../user/userAvatar/userAvatar';
import UserName from '../user/userName/userName';
import CoverPage from '../@coverPage/coverPage';
import { checkLogin } from '../../@utils';
import { useUserInfo } from '../../hooks/useUserInfo';

import {
    LearnTask,
    ParticipatedCourse,
    Website,
    Class,
    InputPin,
    Index,
    Logout,
    Split,
    Login,
    Register,
    Event,
    Course,
    Platform,
    Education,
    PlanPricing,
    LearningDesign,
    ProductExercise,
    ProductLive,
    ProductEvent,
    ContactUs,
    Collections,
    QuestionCollections,
    AiAudioSlides,
    UShowIntroducePanel,
    AssessmentIntroducePanel,
    LearningCircleIntroducePanel,
    ResourcesAndDynamicsIntroducePanel,
    OFficialAnnouncement,
    Faq,
    Academy,
    BindWx,
    BindPhone,
    AITools,
} from './userMenuItem/menuItem';

import LangSwitch from './userMenuItem/menuItemLangSwitch';
import './userMenu.scss';
import UnReadCount from './userMenuItem/menuItemUnreadCount';
import FontSizeSwitch from './userMenuItem/menuItemFontSizeSwitch';
import { PlatformPermission, ToolsSwitch } from '@umu/umu-types';

export enum MENU_ITEM {
    LearnTask,
    ParticipatedCourse,
    Website,
    Class,
    InputPin,
    Index,
    UnreadCount,
    LangSwitch,
    FontSizeSwitch,
    Logout,
    Split,
    Login,
    Register,
    Event,
    Course,
    Platform,
    AiAudioSlides,
    PlanPricing,
    Education,
    LearningDesign,
    ProductExercise,
    ProductLive,
    ProductEvent,
    ContactUs,
    Collections,
    QuestionCollections,
    uShow,
    Assessment,
    LearningCircle,
    ResourcesAndDynamicsIntroduce,
    OFficialAnnouncement,
    Faq,
    Academy,
    BindWx,
    BindPhone,
    AITools,
}

export type IMenuItem = MENU_ITEM | ((props?: { controller: IUserMenuController }) => JSX.Element);
interface IUserMenuProps {
    menuList?: IMenuItem[];
}

const ItemMap = {
    [MENU_ITEM.LearnTask]: LearnTask,
    [MENU_ITEM.ParticipatedCourse]: ParticipatedCourse,
    [MENU_ITEM.Website]: Website,
    [MENU_ITEM.OFficialAnnouncement]: OFficialAnnouncement,
    [MENU_ITEM.Class]: Class,
    [MENU_ITEM.InputPin]: InputPin,
    [MENU_ITEM.Index]: Index,
    [MENU_ITEM.Split]: Split,
    [MENU_ITEM.Faq]: Faq,
    [MENU_ITEM.Academy]: Academy,
    [MENU_ITEM.LangSwitch]: LangSwitch,
    [MENU_ITEM.FontSizeSwitch]: FontSizeSwitch,
    [MENU_ITEM.UnreadCount]: UnReadCount,
    [MENU_ITEM.Logout]: Logout,
    [MENU_ITEM.Login]: Login,
    [MENU_ITEM.Register]: Register,
    [MENU_ITEM.Event]: Event,
    [MENU_ITEM.Course]: Course,
    [MENU_ITEM.PlanPricing]: PlanPricing,
    [MENU_ITEM.Platform]: Platform,
    [MENU_ITEM.AiAudioSlides]: AiAudioSlides,
    [MENU_ITEM.Education]: Education,
    [MENU_ITEM.AITools]: AITools,

    // 官网新页面
    [MENU_ITEM.LearningDesign]: LearningDesign,
    [MENU_ITEM.ProductExercise]: ProductExercise,
    [MENU_ITEM.ProductLive]: ProductLive,
    [MENU_ITEM.ProductEvent]: ProductEvent,

    [MENU_ITEM.ContactUs]: ContactUs,
    [MENU_ITEM.Collections]: Collections,
    [MENU_ITEM.QuestionCollections]: QuestionCollections,
    [MENU_ITEM.uShow]: UShowIntroducePanel,
    [MENU_ITEM.Assessment]: AssessmentIntroducePanel,
    [MENU_ITEM.LearningCircle]: LearningCircleIntroducePanel,
    [MENU_ITEM.ResourcesAndDynamicsIntroduce]: ResourcesAndDynamicsIntroducePanel,
    [MENU_ITEM.BindWx]: BindWx,
    [MENU_ITEM.BindPhone]: BindPhone,
};

const defaultList = [
    MENU_ITEM.Collections,
    MENU_ITEM.QuestionCollections,
    MENU_ITEM.Split,
    MENU_ITEM.LearnTask,
    MENU_ITEM.ParticipatedCourse,
    MENU_ITEM.Class,
    MENU_ITEM.Split,
    MENU_ITEM.UnreadCount,
    MENU_ITEM.Split,
    MENU_ITEM.InputPin,
    MENU_ITEM.Index,
    MENU_ITEM.Website,
    MENU_ITEM.OFficialAnnouncement,
    MENU_ITEM.Faq,
    MENU_ITEM.Academy,
    MENU_ITEM.Split,
    MENU_ITEM.AITools,
    MENU_ITEM.Split,
    MENU_ITEM.LangSwitch,
    MENU_ITEM.FontSizeSwitch,
];

const unLoginDefaultList = [
    MENU_ITEM.Login,
    MENU_ITEM.Register,
    MENU_ITEM.ContactUs,
    MENU_ITEM.Split,
    MENU_ITEM.InputPin,
    MENU_ITEM.Split,
    MENU_ITEM.Index,
    // MENU_ITEM.Event,
    MENU_ITEM.LearningDesign,
    MENU_ITEM.Platform,
    MENU_ITEM.ProductExercise,
    MENU_ITEM.AiAudioSlides,
    MENU_ITEM.uShow,
    MENU_ITEM.Assessment,
    MENU_ITEM.ProductLive,
    MENU_ITEM.LearningCircle,
    MENU_ITEM.ProductEvent,
    MENU_ITEM.Split,
    MENU_ITEM.Education,
    MENU_ITEM.PlanPricing,
    MENU_ITEM.Split,
    MENU_ITEM.ResourcesAndDynamicsIntroduce,
    MENU_ITEM.LangSwitch,
];

if (isSystem('EN')) {
    defaultList.splice(defaultList.indexOf(MENU_ITEM.Academy), 0, MENU_ITEM.Split);
}

function UserMenu(props: IUserMenuProps) {
    const [isShow, setIsShow] = useState(false);
    const [isVisable, setIsVisable] = useState(true);
    const enableAITools: ToolsSwitch = getData(window.pageData?.enterprise_setting, 'setup', 'enable_ai_tools');
    const platformPermission = getData(window.pageData?.data, 'user_info', 'platform_permission') || 0;
    const hasUSEPermission = [PlatformPermission.use, PlatformPermission.umuAndUse].includes(platformPermission);
    const hasUMUPermission = [PlatformPermission.umu, PlatformPermission.umuAndUse].includes(platformPermission);
    const isInUSE = (window as any).isInUSE || isInUse();
    const controller: IUserMenuController = useMemo(() => {
        return {
            hide: () => {
                setIsVisable(false);
            },
            show: () => {
                setIsVisable(true);
            },
            close: () => {
                setIsShow(false);
                return;
            },
            open: () => {
                setIsShow(true);
                return;
            },
        };
    }, [isShow, isVisable]);
    const [userInfo, isReady] = useUserInfo();
    const isLogin = checkLogin(userInfo); // 判断登录用户

    function bindEffect() {
        // useEffect(() => {
        //     setIsShow(props.isShow);
        // }, [props.isShow])
    }

    function renderUserTitle() {
        const versionType = userInfo?.enterprise_info?.version_type || userInfo?.teacher_info?.teacher_status || 0;
        const enterpriseRoleNameMap = [lang('学员'), lang('讲师'), lang('学习负责人'), lang('系统管理员')];
        const roleType = userInfo?.enterprise_info?.role_type || 0;
        const displayName = userInfo?.enterprise_info?.role_name || enterpriseRoleNameMap[roleType - 1] || '';
        if (!getData(userInfo, 'enterprise_info', 'enterprise_id')) {
            switch (String(versionType)) {
                case '1':
                    return <span className="user-enterprise-title">{lang('基本版')}</span>;
                case '2':
                    return <span className="user-enterprise-title user-enterprise-title-pro">{lang('专业版')}</span>;
                case '4':
                    return <span className="user-enterprise-title user-enterprise-title-pro">{lang('VIP专业版')}</span>;
                case '5':
                    return <span className="user-enterprise-title">{lang('教育基本版')}</span>;
                case '6':
                    return (
                        <span className="user-enterprise-title user-enterprise-title-pro">{lang('教育专业版')}</span>
                    );
                default:
                    break;
            }
            return null;
        }
        return <>{displayName ? <span className="user-title">{displayName}</span> : null}</>;
    }

    function renderProfile() {
        if (!isLogin) {
            return null;
        }
        const accInfo = userInfo.phone_number || userInfo.phone || userInfo.email || userInfo.login_name || '';
        let enterpriseName = '';
        const isEnterprise = !!getData(userInfo, 'enterprise_info', 'enterprise_id');
        if (isEnterprise) {
            enterpriseName =
                getData(userInfo, 'enterprise_info', 'show_name') || getData(userInfo, 'enterprise_info', 'real_name');
        }

        return (
            <a
                href={`/profile/#/center${isInUSE && hasUSEPermission ? `?${USEQuery}=1` : ''}`}
                className="panel-user-card"
            >
                <div className="panel-user-card-wrap">
                    <div className="panel-user-avatar" style={{ marginTop: userInfo.show_user_level ? '-0.04rem' : 0 }}>
                        <UserAvatar
                            src={userInfo.avatar}
                            width={60}
                            className="round"
                            userLevelInfo={userInfo.user_level}
                            showUserLevel={userInfo.show_user_level}
                        />
                    </div>
                    <div className="panel-right">
                        <div className="panel-right-top">
                            <div className="panel-user-name">
                                <UserName
                                    className=""
                                    name={userInfo.user_name}
                                    userMedalInfo={userInfo.user_medal}
                                    showUserLevel={userInfo.show_user_level}
                                    userMedalWidth={20}
                                    transcate={true}
                                />
                                {renderUserTitle()}
                            </div>
                        </div>
                        <div className="panel-right-bottom">
                            <div className="panel-user-info">
                                <div className="panel-user-acc truncate-text">{accInfo}</div>
                                <div className="enterprise-name">{enterpriseName}</div>
                            </div>
                            <div className="panel-user-action">
                                {lang('个人中心')}
                                <i className="icon icon-arrow arrow1 gray right size-xs"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </a>
        );
    }

    function renderActionBtns() {
        if (!isLogin) {
            return null;
        }
        const actionBtns = [];
        if (isInUSE) {
            hasUMUPermission &&
                actionBtns.push({
                    className: 'to-umu',
                    href: `${getMHost()}/main`,
                    children: lang('去学习平台'),
                    onClick: () => setIsInUse(false),
                });
        } else {
            hasUSEPermission &&
                actionBtns.push({
                    className: 'to-use',
                    href: `${getMHost()}/use/index`,
                    children: lang('去赋能平台'),
                    onClick: () => setIsInUse(true),
                });
        }
        return (
            <div className="panel-action-btns">
                {actionBtns.map((item, index) => (
                    <a
                        key={index}
                        className={`panel-action-btn ${item.className}`}
                        href={item.href}
                        onClick={item.onClick}
                    >
                        {item.children}
                    </a>
                ))}
            </div>
        );
    }

    function renderMenuItem(type: IMenuItem, index: number) {
        const Component = typeof type === 'number' ? ItemMap[type] : type;
        return (
            <li className="user-menu-item" key={index}>
                <Component controller={controller}></Component>
            </li>
        );
    }

    const renderMenuList = useCallback(() => {
        let list = props.menuList ? [...props.menuList] : isLogin ? [...defaultList] : [...unLoginDefaultList];

        if (!isCanShowFontSizeSwitch()) {
            list = list.filter((item) => item !== MENU_ITEM.FontSizeSwitch);
        }

        if (isLogin) {
            const isEnterprise = !!getData(userInfo, 'enterprise_info', 'enterprise_id');
            const hideAITools = isEnterprise && enableAITools === ToolsSwitch.notOpen; // 只有未开通 AI Tools 的企业不展示
            if (isFromMideaMiniApp()) {
                list.push(MENU_ITEM.BindWx);
                list.push(MENU_ITEM.BindPhone);
            }
            if (hideAITools) {
                list.splice(list.indexOf(MENU_ITEM.AITools), 2);
            }
            list.push(MENU_ITEM.Logout);
            list.unshift(MENU_ITEM.Split);
        }

        return <div className="user-menu-list">{list.map((type, index) => renderMenuItem(type, index))}</div>;
    }, [isLogin, props.menuList, defaultList, unLoginDefaultList]);

    function renderMenuContent() {
        if (!isShow) {
            return null;
        }
        const hidden = isVisable ? '' : 'hidden';
        return (
            <Portal preventBodyScroll={true}>
                <CoverPage clsName="user-menu-panel-cover-page">
                    <div className={`user-menu-panel ${hidden}`}>
                        <div className="panel-shadow"></div>
                        <div className="user-menu-content">
                            <span className="close-btn" onClick={controller.close}></span>
                            {renderProfile()}
                            {renderActionBtns()}
                            {renderMenuList()}
                        </div>
                    </div>
                </CoverPage>
            </Portal>
        );
    }

    function render() {
        const showClassName = isReady ? 'show' : '';
        return (
            <div className="user-menu">
                <div className={`user-info ${showClassName}`} onClick={() => setIsShow(true)}>
                    <If condition={isLogin}>
                        <div className="user-info-wrap">
                            <UserAvatar
                                src={userInfo.avatar}
                                width={30}
                                className="round"
                                userMedalInfo={userInfo.user_medal}
                                showUserLevel={userInfo.show_user_level}
                                userLevelSize="M"
                            />
                        </div>
                    </If>
                    <If condition={!isLogin}>
                        <div>{lang('登录')}</div>
                    </If>
                </div>
                {renderMenuContent()}
            </div>
        );
    }

    bindEffect();
    return render();
}

export default UserMenu;
